import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DetailSectionIDs, SectionIDs, Sections } from '../data/Sections';
import gsap from 'gsap';
import FocusPoints from '../data/FocusPoints';

export type DetailViewData = {
    detailViewVisible: boolean,

    // can be floating point for transitions
    detailIndex: number,
    activeDetailID: DetailSectionIDs,
    maxDetailIndex: number,
    detailSections: DetailSectionIDs[],
    advancedModeOn: boolean;
    setAdvancedMode: (value: boolean) => void;
    getDetailIndexForID: (id: DetailSectionIDs) => number,
    setDetailSectionID: (c: DetailSectionIDs) => void;
    activeFocusID: number;
    setActiveFocusID: (id: FocusPoints) => void;
    activatedDetailIds: {[id: string]: boolean};
    showExpectationBox: boolean;
    setShowExpectationBox: (value: boolean) => void;
    showRealityBox: boolean;
    setShowRealityBox: (value: boolean) => void;
};

export const DetailViewContext = createContext<DetailViewData>({
  detailIndex: 0,
  maxDetailIndex: 0,
  activeDetailID: DetailSectionIDs.None,
  detailViewVisible: false,
  detailSections: [],
  advancedModeOn: false,
  setAdvancedMode: () => ({}),
  getDetailIndexForID: () => 0,
  setDetailSectionID: () => ({}),
  activeFocusID: -1,
  setActiveFocusID: () => ({}),
  activatedDetailIds: {},
  showExpectationBox: false,
  setShowExpectationBox: () => ({}),
  showRealityBox: false,
  setShowRealityBox: () => ({})
});

export default function DetailViewContextProvider({ children }: JSX.ElementChildrenAttribute): JSX.Element
{
  // this is the detail "3D sphere info" flow that overlays
  const [ detailViewVisible, setDetailViewVisible ] = useState(false);
  const [ detailIndex, setDetailIndex ] = useState(0);
  const [ activeDetailID, _setDetailSectionID ] = useState(DetailSectionIDs.None);
  const [ activeFocusID, _setActiveFocusID ] = useState(FocusPoints.None);
  const [ showExpectationBox, setShowExpectationBox ] = useState(true);
  const [ showRealityBox, setShowRealityBox ] = useState(false);
  const [advancedModeOn, setAdvancedMode ] = useState(false);

  const setActiveFocusID = useCallback(id => {
    _setActiveFocusID(id);
  }, [_setActiveFocusID]);

  const detailSections = useMemo(() =>
  {
    for (const { detailSections, id } of Sections) {
      if (id === SectionIDs.Science && detailSections)
        return detailSections;
    }
    return [];
  }, []);

  const indexAnim = useMemo(() =>
  {
    return { value: 0, active: false };
  }, []);

  const activatedDetailIds = useRef<{[id: string]: boolean}>({});

  const setDetailSectionID = useCallback((value: DetailSectionIDs) =>
  {
    if (value === DetailSectionIDs.None) {
      setDetailViewVisible(false);
      setActiveFocusID(FocusPoints.None);
      indexAnim.active = false;
    }
    else {
      setDetailViewVisible(true);
    }
    _setDetailSectionID(value);
  }, [indexAnim, setActiveFocusID]);

  useEffect(() =>
  {
    const index = detailSections.indexOf(activeDetailID);
    if (index < 0) return;

    if (indexAnim.active) {
      gsap.killTweensOf(indexAnim);
      gsap.to(indexAnim, { value: index, duration: 1, onUpdate: () => setDetailIndex(indexAnim.value) });
    }
    else {
      setDetailIndex(index);
      indexAnim.value = index;
      indexAnim.active = true;
    }
  }, [ activeDetailID, detailSections, indexAnim ]);

  const getDetailIndexForID = useCallback((id: DetailSectionIDs) =>
    detailSections.indexOf(id)
  , [ detailSections ]);

  const maxDetailIndex = useMemo(() => detailSections.length - 1, [ detailSections.length ]);

  useEffect(() => {
    activatedDetailIds.current[activeDetailID] = true;
  }, [activeDetailID]);

  return (
    <DetailViewContext.Provider
      value={{
        detailViewVisible,
        detailIndex,
        activeDetailID,
        maxDetailIndex,
        detailSections,
        advancedModeOn,
        setAdvancedMode,
        getDetailIndexForID,
        setDetailSectionID,
        // These are the "advanced mode" (aka "dark mode") states
        activeFocusID,
        setActiveFocusID,
        showExpectationBox,
        setShowExpectationBox,
        showRealityBox,
        setShowRealityBox,
        activatedDetailIds: activatedDetailIds.current
      }}
    >
      {children}
    </DetailViewContext.Provider>
  );
}

export const useDetailViewContext: () => DetailViewData = () => useContext(DetailViewContext);
